import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ChevronRight, ChevronDown, Building2, Layers, Home, CheckCircle,
    Clock, AlertCircle, Save, X, TrendingUp, Check, Minus
} from 'lucide-react';
import { Button, Card } from '@components/ui';
import { getConstructionById } from '@mocks/insaatlar';
import { getBlocksByConstructionId } from '@mocks/bloklar';
import {
    getBlokDaireleri, getDaireIlerleme, anaKalemSablonlari, durumlar
} from '@mocks/daireIlerleme';
import { toast } from 'react-toastify';
import clsx from 'clsx';

export default function DaireDetay() {
    const { id, blokId, daireId } = useParams();
    const navigate = useNavigate();
    const [construction, setConstruction] = useState(null);
    const [blok, setBlok] = useState(null);
    const [daire, setDaire] = useState(null);
    const [ilerleme, setIlerleme] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Expand/collapse state — sadece bir kalem açık olabilir
    const [expandedKalem, setExpandedKalem] = useState(null);

    // Düzenleme state — alt kalem değerleri
    const [editValues, setEditValues] = useState({});
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            const c = getConstructionById(id);
            if (c) {
                setConstruction(c);
                const bloklar = getBlocksByConstructionId(id);
                setBlok(bloklar.find(bl => bl.id === blokId) || null);
                const blokData = getBlokDaireleri(blokId);
                if (blokData) {
                    const tumDaireler = blokData.katlar.flatMap(k => k.daireler);
                    setDaire(tumDaireler.find(d => d.id === daireId) || null);
                }
                const il = getDaireIlerleme(daireId);
                setIlerleme(il);
                if (il) {
                    const init = {};
                    il.kalemler.forEach(k => {
                        k.altKalemler.forEach(ak => {
                            init[ak.altKalemId] = { deger: ak.deger, tip: ak.tip };
                        });
                    });
                    setEditValues(init);
                }
            }
            setIsLoading(false);
        }, 400);
        return () => clearTimeout(timer);
    }, [id, blokId, daireId]);

    // Alt kalem değeri değiştir
    const handleEdit = useCallback((altKalemId, newDeger) => {
        setEditValues(prev => ({ ...prev, [altKalemId]: { ...prev[altKalemId], deger: newDeger } }));
        setHasChanges(true);
    }, []);

    // Ana kalem yüzdesini alt kalemlerden dinamik hesapla
    const getAnaKalemYuzde = useCallback((anaKalemId) => {
        const sablon = anaKalemSablonlari.find(k => k.id === anaKalemId);
        if (!sablon) return 0;
        let toplam = 0;
        let count = 0;
        sablon.altKalemler.forEach(alt => {
            const val = editValues[alt.id];
            if (!val) return;
            count++;
            if (val.tip === 'adet') toplam += val.deger ? 1 : 0;
            else toplam += (val.deger || 0) / 100;
        });
        return count > 0 ? Math.round((toplam / count) * 100) : 0;
    }, [editValues]);

    // Genel ilerleme
    const genelIlerleme = useMemo(() => {
        const yuzdelar = anaKalemSablonlari.map(k => getAnaKalemYuzde(k.id));
        return yuzdelar.length > 0 ? Math.round(yuzdelar.reduce((a, b) => a + b, 0) / yuzdelar.length) : 0;
    }, [getAnaKalemYuzde]);

    const genelDurum = genelIlerleme === 100 ? 'tamamlandi' : genelIlerleme > 0 ? 'devam_ediyor' : 'baslanmadi';

    // Kaydet
    const handleSave = () => {
        toast.success(`${daire?.no} dairesi güncellendi`);
        setHasChanges(false);
    };

    // Helper
    const getProgressColor = (y) => y >= 75 ? 'bg-success' : y >= 40 ? 'bg-primary' : y > 0 ? 'bg-warning' : 'bg-bg-tertiary';
    const getProgressTextColor = (y) => y >= 75 ? 'text-success' : y >= 40 ? 'text-primary' : y > 0 ? 'text-warning' : 'text-text-muted';

    // Kalem istatistikleri
    const kalemStats = useMemo(() => {
        let t = 0, d = 0, b = 0;
        anaKalemSablonlari.forEach(k => {
            const y = getAnaKalemYuzde(k.id);
            if (y === 100) t++;
            else if (y > 0) d++;
            else b++;
        });
        return { tamamlanan: t, devamEden: d, baslanmadi: b };
    }, [getAnaKalemYuzde]);

    if (isLoading) {
        return (
            <div className="p-4 md:p-6 space-y-4 animate-pulse">
                <div className="h-6 w-64 bg-bg-tertiary rounded" />
                <div className="h-32 bg-bg-tertiary rounded-xl" />
                {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-bg-tertiary rounded-xl" />)}
            </div>
        );
    }

    if (!construction || !blok || !daire) {
        return (
            <div className="p-4 md:p-6 text-center py-16">
                <Home size={64} className="mx-auto mb-4 text-text-muted opacity-40" />
                <h2 className="text-xl font-bold text-text-primary mb-2">Daire Bulunamadı</h2>
                <Button variant="primary" onClick={() => navigate('/insaat/projeler')}>Projelere Dön</Button>
            </div>
        );
    }

    const durumBilgi = durumlar[genelDurum];

    return (
        <div className="p-3 md:p-6 space-y-4 md:space-y-6 pb-24 md:pb-6">
            {/* Breadcrumb — mobilde kompakt */}
            <div className="flex items-center gap-1.5 text-xs md:text-sm text-text-muted flex-wrap">
                <button onClick={() => navigate('/insaat/projeler')} className="hover:text-primary transition-colors">İnşaatlar</button>
                <ChevronRight size={12} />
                <button onClick={() => navigate(`/insaat/projeler/${id}`)} className="hover:text-primary transition-colors truncate max-w-[80px] md:max-w-none">{construction.name}</button>
                <ChevronRight size={12} />
                <button onClick={() => navigate(`/insaat/projeler/${id}/blok/${blokId}`)} className="hover:text-primary transition-colors">{blok.name}</button>
                <ChevronRight size={12} />
                <span className="text-text-primary font-medium">{daire.no}</span>
            </div>

            {/* ====== Üst Özet — Mobil Optimize ====== */}
            <div className="bg-bg-secondary rounded-2xl border border-border-light overflow-hidden">
                <div className="p-4 md:p-6">
                    <div className="flex items-center justify-between gap-4">
                        {/* Sol: Info */}
                        <div className="flex items-center gap-3 min-w-0">
                            <div className={clsx(
                                'w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center shrink-0 shadow-sm',
                                genelIlerleme >= 75 ? 'bg-success/10' : genelIlerleme >= 40 ? 'bg-primary/10' :
                                    genelIlerleme > 0 ? 'bg-warning/10' : 'bg-bg-tertiary'
                            )}>
                                <Home size={24} className={getProgressTextColor(genelIlerleme)} />
                            </div>
                            <div className="min-w-0">
                                <h1 className="text-lg md:text-2xl font-bold text-text-primary">Daire {daire.no}</h1>
                                <div className="flex items-center gap-2 text-xs md:text-sm text-text-muted mt-0.5 flex-wrap">
                                    <span>{blok.name}</span>
                                    <span>•</span>
                                    <span>{daire.kat}. Kat</span>
                                    <span>•</span>
                                    <span className="px-1.5 py-0.5 bg-bg-tertiary rounded text-2xs md:text-xs font-medium text-text-primary">{daire.tip}</span>
                                </div>
                            </div>
                        </div>

                        {/* Sağ: Circular Progress */}
                        <div className="relative w-16 h-16 md:w-20 md:h-20 shrink-0">
                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 80 80">
                                <circle cx="40" cy="40" r="32" fill="none" strokeWidth="5" className="stroke-bg-tertiary" />
                                <circle cx="40" cy="40" r="32" fill="none" strokeWidth="5" strokeLinecap="round"
                                    className={clsx(
                                        genelIlerleme >= 75 ? 'stroke-success' : genelIlerleme >= 40 ? 'stroke-primary' :
                                            genelIlerleme > 0 ? 'stroke-warning' : 'stroke-bg-tertiary'
                                    )}
                                    strokeDasharray={`${(genelIlerleme / 100) * 201} 201`}
                                    style={{ transition: 'stroke-dasharray 0.5s ease' }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-sm md:text-base font-bold text-text-primary">%{genelIlerleme}</span>
                            </div>
                        </div>
                    </div>

                    {/* Mini stat bar */}
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border-light/50">
                        <span className={clsx('px-2.5 py-1 rounded-full text-xs font-medium', durumBilgi.renk)}>{durumBilgi.label}</span>
                        <div className="flex items-center gap-3 text-xs text-text-muted ml-auto">
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-success" /> {kalemStats.tamamlanan} bitti</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-warning" /> {kalemStats.devamEden} devam</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400" /> {kalemStats.baslanmadi} bekliyor</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ====== İlerleme Kalemleri — Expandable Cards ====== */}
            <div className="space-y-2">
                {anaKalemSablonlari.map(anaKalem => {
                    const yuzde = getAnaKalemYuzde(anaKalem.id);
                    const isExpanded = expandedKalem === anaKalem.id;
                    const durum = yuzde === 100 ? 'tamamlandi' : yuzde > 0 ? 'devam_ediyor' : 'baslanmadi';
                    const tamamlananAlt = anaKalem.altKalemler.filter(alt => {
                        const val = editValues[alt.id];
                        if (!val) return false;
                        return val.tip === 'adet' ? val.deger : val.deger >= 100;
                    }).length;

                    return (
                        <div key={anaKalem.id} className={clsx(
                            'rounded-xl border transition-all duration-200 overflow-hidden',
                            isExpanded ? 'border-primary/30 shadow-md' : 'border-border-light',
                            'bg-bg-secondary'
                        )}>
                            {/* Ana Kalem Header — Tıklanabilir */}
                            <button
                                onClick={() => setExpandedKalem(isExpanded ? null : anaKalem.id)}
                                className="w-full flex items-center gap-3 p-3.5 md:p-4 text-left active:bg-bg-hover transition-colors"
                            >
                                <span className="text-xl md:text-2xl w-8 text-center shrink-0">{anaKalem.ikon}</span>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <h3 className="text-sm md:text-base font-semibold text-text-primary truncate">{anaKalem.name}</h3>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span className="text-xs text-text-muted">{tamamlananAlt}/{anaKalem.altKalemler.length}</span>
                                            <span className={clsx('text-sm font-bold', getProgressTextColor(yuzde))}>%{yuzde}</span>
                                        </div>
                                    </div>
                                    {/* Progress bar */}
                                    <div className="h-1.5 bg-bg-tertiary rounded-full overflow-hidden">
                                        <div className={clsx('h-full rounded-full transition-all duration-500', getProgressColor(yuzde))} style={{ width: `${yuzde}%` }} />
                                    </div>
                                </div>

                                <ChevronDown size={18} className={clsx(
                                    'text-text-muted shrink-0 transition-transform duration-200',
                                    isExpanded && 'rotate-180'
                                )} />
                            </button>

                            {/* Alt Kalemler — Expand Area */}
                            {isExpanded && (
                                <div className="border-t border-border-light/50 bg-bg-primary/50">
                                    <div className="p-2 md:p-3 space-y-0.5">
                                        {anaKalem.altKalemler.map(alt => {
                                            const val = editValues[alt.id] || { deger: alt.tip === 'adet' ? false : 0, tip: alt.tip };
                                            return (
                                                <AltKalemSatir
                                                    key={alt.id}
                                                    alt={alt}
                                                    deger={val.deger}
                                                    onEdit={(newVal) => handleEdit(alt.id, newVal)}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* ====== Sticky Kaydet Butonu (Mobil) ====== */}
            {hasChanges && (
                <div className="fixed bottom-0 left-0 right-0 md:static p-3 md:p-0 bg-bg-primary/95 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none border-t md:border-t-0 border-border-light z-50">
                    <div className="flex items-center gap-3 max-w-screen-lg mx-auto">
                        <Button variant="ghost" size="md" onClick={() => { setHasChanges(false); }} icon={<X size={16} />} className="flex-1 md:flex-none">
                            İptal
                        </Button>
                        <Button variant="primary" size="md" onClick={handleSave} icon={<Save size={16} />} className="flex-1 md:flex-none">
                            Değişiklikleri Kaydet
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ============================================
// Alt Kalem Satırı — Checkbox veya Slider
// ============================================
function AltKalemSatir({ alt, deger, onEdit }) {
    if (alt.tip === 'adet') {
        // Checkbox — tıklanabilir, yapıldı/yapılmadı
        return (
            <button
                onClick={() => onEdit(!deger)}
                className={clsx(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all',
                    'active:scale-[0.98]',
                    deger ? 'bg-success/5' : 'hover:bg-bg-hover'
                )}
            >
                <div className={clsx(
                    'w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 transition-all',
                    deger ? 'bg-success border-success' : 'border-border-light bg-bg-primary'
                )}>
                    {deger && <Check size={14} className="text-white" />}
                </div>
                <span className={clsx(
                    'text-sm flex-1 text-left transition-colors',
                    deger ? 'text-text-primary line-through opacity-60' : 'text-text-primary'
                )}>
                    {alt.name}
                </span>
                {deger && <CheckCircle size={14} className="text-success shrink-0" />}
            </button>
        );
    }

    // Yüzde — Slider
    return (
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-bg-hover transition-colors">
            <div className={clsx(
                'w-6 h-6 rounded-lg flex items-center justify-center shrink-0',
                deger >= 100 ? 'bg-success/10' : deger > 0 ? 'bg-warning/10' : 'bg-bg-tertiary'
            )}>
                {deger >= 100 ? <Check size={14} className="text-success" /> : <Minus size={14} className="text-text-muted" />}
            </div>
            <span className="text-sm text-text-primary flex-1 min-w-0 truncate">{alt.name}</span>
            <div className="flex items-center gap-2 shrink-0 w-28 md:w-36">
                <input
                    type="range"
                    min="0" max="100" step="5"
                    value={deger}
                    onChange={(e) => onEdit(parseInt(e.target.value))}
                    className="flex-1 accent-primary h-1.5"
                />
                <span className={clsx('text-xs font-mono font-bold w-8 text-right', getProgressTextColorStatic(deger))}>
                    %{deger}
                </span>
            </div>
        </div>
    );
}

function getProgressTextColorStatic(y) {
    if (y >= 100) return 'text-success';
    if (y >= 40) return 'text-primary';
    if (y > 0) return 'text-warning';
    return 'text-text-muted';
}
